import React from 'react';
import {
    TextField,
    BooleanField,
    DateField,
    SimpleShowLayout,
    SimpleForm,
    TextInput,
    LongTextInput,
    SelectInput,
    DisabledInput,
    Toolbar,
    required,
    email,
    regex
} from 'admin-on-rest';
import {DependentInput} from 'aor-dependent-input';
import List from '../admin/List';
import Show from '../admin/Show';
import Create from '../admin/Create';
import Edit from '../admin/Edit';
import FilterWithSubmit from '../admin/FilterWithSubmit';
import ListTableContainer from '../components/ListTableContainer';
import ViewButton from '../buttons/ViewButton';
import EditButton from '../buttons/EditButton';
import LoyaltyAccountAccessFields from '../components/LoyaltyAccountAccessFields';
import SaveButtonWithCondition from '../buttons/SaveButtonWithCondition';
import LoyaltyAccountIdInputForm from '../components/LoyaltyAccountIdInputForm';
import VirtualizedSelectInput from '../components/VirtualizedSelectInput';
import TransactionTable from '../components/TransactionTable';
import TooltipIcon from '../components/TooltipIcon';
import ReactTooltip from 'react-tooltip';
import {US_STATES as usStates} from '../shared/usStates';
import {viewStyles} from '../shared/styles-view';
import Avatar from 'material-ui/Avatar';
import Icon from 'material-ui/svg-icons/action/card-giftcard';

export const fuseLoyaltyAccountIcon = Icon;

const styles = {
    filterInput: {minWidth: 400},
    fieldSmall: {display: 'inline-block', maxWidth: 200, margin: '0 1em 0 0', verticalAlign: 'top', overflow: 'hidden'}
};

const MakeTransactionTable = ({record}) => {
    const accountId = (record.id) ? record.id : null;
    return (
        <TransactionTable {...record} accountId={accountId}/>
    );
};

/*
 * View: List
 */

const LoyaltyAccountFilter = (props) => (
    <FilterWithSubmit {...props} filterButton="Find Account(s)">
        <TextInput source="loyalty_telephone" label="Search Loyalty Accounts by Telephone"
                   alwaysOn style={viewStyles.filterLeft} elStyle={styles.filterInput}/>
        <TextInput source="loyalty_email" label="Search Loyalty Accounts by Email"
                   alwaysOn style={viewStyles.filterLeft} elStyle={styles.filterInput}/>
        <TextInput source="loyalty_id" label="Search Loyalty Accounts by ID"
                   alwaysOn style={viewStyles.filterLeft} elStyle={styles.filterInput}/>
    </FilterWithSubmit>
);

// Set title for List Loyalty Account(s)
const ListTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                                className="avatar-title"/>Loyalty Accounts</span>;

const ListDescription = () => {
    return (
        <span>
            <p>Loyalty Account Customers earn Reward Points and Ranks by purchases, with the ability to earn more
                points based on the total purchases or amount spent.</p>
            <p>Loyalty Ranks and Incentive Tiers must exist before creating a Loyalty Account. If Ranks and Tiers do
                not exist, create at least
                one <a href="#/LoyaltyRanks/create" style={viewStyles.fieldLink}>Rank</a>&nbsp;
                and <a href="#/LoyaltyIncentiveTiers/create" style={viewStyles.fieldLink}>Tier</a> first.</p>
        </span>
    );
};

const ListDescriptionViewOnly = () => {
    return (
        <span>
            <p>Loyalty Account Customers earn Reward Points and Ranks by purchases, with the ability to earn more
                points based on the total purchases or amount spent.</p>
        </span>
    );
};

const ListColumns = [
    {source: 'id', label: 'ID', type: 'text'},
    {source: 'first_name', label: 'First Name', type: 'text'},
    {source: 'last_name', label: 'Last Name', type: 'text'},
    {
        source: 'active',
        label: 'Active',
        component: <BooleanField source="active" label="Active"/>,
        filterable: false,
        width: 90
    },
    {
        source: 'registration_date',
        label: 'Registered',
        component: <DateField source="registration_date" label="Registered"/>
    },

    {source: 'id', label: '', style: viewStyles.hidden},

    {source: '', label: '', component: <ViewButton/>, filterable: false, sortable: false, width: 80},
    {source: '', label: '', component: <EditButton/>, filterable: false, sortable: false, width: 80}
];

const ListColumnsViewOnly = [
    {source: 'id', label: 'ID', type: 'text'},
    {source: 'first_name', label: 'First Name', type: 'text'},
    {source: 'last_name', label: 'Last Name', type: 'text'},
    {
        source: 'active',
        label: 'Active',
        component: <BooleanField source="active" label="Active"/>,
        filterable: false,
        width: 90
    },
    {
        source: 'registration_date',
        label: 'Registered',
        component: <DateField source="registration_date" label="Registered"/>
    },

    {source: 'id', label: '', style: viewStyles.hidden},

    {source: '', label: '', component: <ViewButton/>, filterable: false, sortable: false, width: 80}
];

export const fuseLoyaltyAccountList = (props) => (
    <List
        title={ListTitle}
        listType="table"
        desc={<ListDescription/>}
        filters={<LoyaltyAccountFilter/>}
        requiresFilter={true}
        allowQueryString={false}
        pagination={null}
        {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumns}
            perPage={20}
            currentSort={{field: 'id', order: 'ASC'}}
        />
    </List>
);

export const fuseLoyaltyAccountListViewOnly = (props) => (
    <List
        title={ListTitle}
        listType="table"
        desc={<ListDescriptionViewOnly/>}
        filters={<LoyaltyAccountFilter/>}
        requiresFilter={true}
        allowQueryString={false}
        pagination={null}
        {...props}
    >
        <ListTableContainer
            {...props}
            columns={ListColumnsViewOnly}
            perPage={20}
            currentSort={{field: 'id', order: 'ASC'}}
        />
    </List>
);

/*
 * View: Show
 */

// Set title for Show Loyalty Account
const ShowTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>{record ? `${record.id}` : 'Loyalty Account'}</span>;
};

const ShowDescription = () => {
    return (
        <span>
            <p>Review Loyalty Account details and transactions below. To edit this Loyalty Account,
                click <strong>Edit</strong> above.</p>
        </span>
    );
};

/*
 * Show Loyalty Account Table
 */
export const fuseLoyaltyAccountShow = (props) => (
    <Show title={<ShowTitle/>} desc={<ShowDescription/>} {...props}>
        <SimpleShowLayout>
            <TextField source="id" label="ID"/>

            <span style={viewStyles.fieldHeader}>Customer Points & Ranking</span>
            <TextField source="rank.name" label="Rank" style={viewStyles.rowShowField}/>
            <TextField source="current_rank_points" label="Rank Points" style={viewStyles.rowShowField}/>
            <TextField source="current_reward_points" label="Reward Points" style={viewStyles.rowShowField}/>

            <span style={viewStyles.fieldHeader}>Customer Information</span>
            <TextField source="first_name" label="First Name" style={viewStyles.rowShowField}/>
            <TextField source="last_name" label="Last Name" style={viewStyles.rowShowField}/>
            <TextField source="address_1" label="Address 1"/>
            <TextField source="address_2" label="Address 2"/>
            <TextField source="city" label="City" style={viewStyles.rowShowField}/>
            <TextField source="state" label="State" style={viewStyles.rowShowField}/>
            <TextField source="zip" label="Zip" style={viewStyles.rowShowField}/>
            <TextField source="country" label="Country" style={viewStyles.rowShowField}/>
            <TextField source="email" label="Email"/>
            <TextField source="telephone" label="Phone"/>
            <BooleanField source="active" label="Active" style={viewStyles.rowShowField}
                          elStyle={styles.fieldSmall}/>
            <TextField source="notes" label="Notes"/>

            <span style={viewStyles.fieldHeader}>Admin Information</span>
            <BooleanField source="fraud_lock" label="Fraud Locked"
                          style={viewStyles.rowShowField} elStyle={styles.fieldSmall}/>
            <BooleanField source="temp_lock" label="Temp Locked"
                          style={viewStyles.rowShowField} elStyle={styles.fieldSmall}/>
            <BooleanField source="override_locks" label="Override All Locks"
                          style={viewStyles.rowShowFieldWide} elStyle={styles.fieldSmall}/>
            <hr style={viewStyles.break}/>
            <DateField source="customer.registration_date" label="Registered"/>

            <TextField source="modified_local_str" label="Last Modified"/>
            {<MakeTransactionTable/>}
        </SimpleShowLayout>
    </Show>
);

/*
 * View: Create form
 */

// Set title for Create Loyalty Account
const CreateTitle = <span><Avatar icon={<Icon/>} style={viewStyles.avatar} className="avatar-title"/>Create a Loyalty Account</span>;

const CreateDescription = () => {
    return (
        <span>
            <p>Create a Loyalty Account. To create a Loyalty Account ID, select an Account Prefix and enter a number
                within the available range. If available, add additional information for the new Account.</p>
            <p>Only an ID, Email, and State are required for a Loyalty Account.</p>
            <p>Loyalty Ranks and Incentive Tiers must exist before creating a Loyalty Account. If Ranks and Tiers do
                not exist, create at least
                one <a href="#/LoyaltyRanks/create" style={viewStyles.fieldLink}>Rank</a>&nbsp;
                and <a href="#/LoyaltyIncentiveTiers/create" style={viewStyles.fieldLink}>Tier</a> first.</p>
        </span>
    );
};

const CreateToolbar = props => <Toolbar {...props} >
    <SaveButtonWithCondition label="Create Loyalty Account" field="id" fieldComparison="exists" fieldCondition="true"
                             submitOnEnter={false}/>
</Toolbar>;

// Handle event from Account ID creation
const handleAddId = (e, index, value) => {
};

// Set defaults for Create form
const defaultCreateValues = {
    active: true,
    fraud_lock: false,
    override_locks: false,
    temp_lock: false,
    reward_points: 0,
    rank_points: 0
};

const validateName = (value) => {
    let error = false;
    // Name fields may contain only alphanumeric characters, spaces, hyphens, apostrophes
    if (/[^A-Za-z0-9- ']/.test(value)) {
        error = 'Must not contain special characters';
    }
    return error;
};

/*
 * Create Loyalty Account form
 */
export const fuseLoyaltyAccountCreate = (props) => (
    <Create {...props} title={CreateTitle} desc={<CreateDescription/>}>
        <SimpleForm redirect="list" defaultValue={defaultCreateValues} toolbar={<CreateToolbar/>}>

            <LoyaltyAccountIdInputForm
                {...props}
                labelId="Enter an ID number within the range"
                labelSubmit="Check ID Availability"
                onAddId={handleAddId}
            />

            <DependentInput dependsOn="id">

                <hr style={viewStyles.divider}/>

                <span style={viewStyles.fieldHeader}>
                    ID / Email / State
                </span>
                <span style={viewStyles.fieldDescription}>
                    Only an ID, Email, and State are required for a Loyalty Account.
                </span>

                <DisabledInput source="id" label="Loyalty Account ID"/>

                <TextInput source="email" label="Email" type="email" validate={[required, email]}/>

                <VirtualizedSelectInput
                    addField={true}
                    label="State*"
                    choices={usStates}
                    source="state"
                    optionText="abbreviation"
                    optionValue="abbreviation"
                    errorMessage="Required"
                    required={true}
                    style={viewStyles.rowField}
                    options={{
                        clearable: false,
                        labelKey: 'abbreviation',
                        className: 'inline short',
                        placeholder: 'State*',
                        required: true
                    }}
                />

                <hr style={viewStyles.divider}/>

                <TextInput source="first_name" label="First Name"
                           validate={[validateName]}
                           style={viewStyles.rowField}
                />

                <TextInput source="last_name" label="Last Name"
                           validate={[validateName]}
                           style={viewStyles.rowField}
                />

                <LongTextInput source="address_1" label="Address Line 1"/>

                <LongTextInput source="address_2" label="Address Line 2"/>

                <TextInput source="city" label="City" style={viewStyles.rowField}/>

                <TextInput source="zip" label="Zip Code"
                           validate={[regex(/^\d{5}$/, 'Must be a valid Zip Code')]}
                           style={{...viewStyles.rowField, ...styles.fieldSmall}}/>

                <TextInput source="country" label="Country" style={viewStyles.rowField}/>

                <TextInput source="telephone" label="Telephone"
                           validate={[regex(/^\d{10}$/, 'Must be 10 digits')]}
                           style={viewStyles.rowField}/>

                <SelectInput source="active" label="Loyalty Account is Active"
                             choices={[
                                 {id: true, name: 'Yes'},
                                 {id: false, name: 'No'}
                             ]}/>
                <span style={viewStyles.fieldDescription}>
                    Select Yes or No.
                    <TooltipIcon dataFor="ttActive"/>
                    <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        If <em>Active</em>, this Loyalty Account may be credited during purchases on the POS System.
                        <br/>
                        If <em>Inactive</em>, this Loyalty Account may not be credited during purchases on the POS System.
                    </ReactTooltip>
                </span>

                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

                <SelectInput source="fraud_lock" label="Fraud Locked"
                             style={viewStyles.rowField}
                             choices={[
                                 {id: true, name: 'True'},
                                 {id: false, name: 'False'}
                             ]}/>
                <SelectInput source="temp_lock" label="Temp Locked"
                             style={viewStyles.rowField}
                             choices={[
                                 {id: true, name: 'True'},
                                 {id: false, name: 'False'}
                             ]}/>
                <SelectInput source="override_locks" label="Override Locks"
                             style={viewStyles.rowField}
                             choices={[
                                 {id: true, name: 'True'},
                                 {id: false, name: 'False'}
                             ]}/>

                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

                <span style={viewStyles.fieldHeader}>
                    Reward Points and Rank
                    <TooltipIcon dataFor="ttReward"/>
                    <ReactTooltip id="ttReward" type="light" effect="solid" multiline={true} border={true}
                                  className="tooltip">
                        <strong>Reward Points</strong> and <strong>Rank Points</strong> are accumulated when a
                        customer applies a Loyalty Account during purchase. If logged in as a user with full Loyalty
                        access, points may be added upon account creation.
                    </ReactTooltip>
                </span>
                <hr style={viewStyles.break}/>

                <LoyaltyAccountAccessFields formType="create"/>

                <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

                <LongTextInput source="notes" label="Account Notes"/>
                <span style={viewStyles.fieldDescription}>
                    Enter any notes regarding this Account.
                </span>

                <span style={viewStyles.fieldInstructions}>
                    After clicking <strong>Save</strong>, you will be redirected to the Loyalty Accounts Search form.
                </span>

            </DependentInput>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

        </SimpleForm>
    </Create>
);

/*
 * View: Edit form
 */

// Set title for Edit Loyalty Account
const EditTitle = ({record}) => {
    return <span><Avatar icon={<Icon/>} style={viewStyles.avatar}
                         className="avatar-title"/>Edit {record ? `${record.id}` : 'Loyalty Account'}</span>;
};

const EditDescription = () => {
    return (
        <span>
            <p>Modify fields below. Click <strong>Save</strong> to store this Loyalty Account.</p>
        </span>
    );
};

/*
 * Edit Loyalty Account form
 */
export const fuseLoyaltyAccountEdit = (props) => (
    <Edit {...props} title={<EditTitle/>} desc={<EditDescription/>}>
        <SimpleForm>
            <TextField source="id" label="Loyalty Account ID"/>

            <TextInput source="first_name" label="First Name"
                       validate={[validateName]}
                       style={viewStyles.rowField}
            />

            <TextInput source="last_name" label="Last Name"
                       validate={[validateName]}
                       style={viewStyles.rowField}
            />

            <TextInput source="email" label="Email" type="email" validate={[email]}/>

            <LongTextInput source="address_1" label="Address Line 1"/>

            <LongTextInput source="address_2" label="Address Line 2"/>

            <TextInput source="city" label="City" style={viewStyles.rowField}/>

            <VirtualizedSelectInput
                addField={true}
                label="State*"
                choices={usStates}
                source="state"
                optionText="abbreviation"
                optionValue="abbreviation"
                errorMessage="Required"
                required={true}
                style={viewStyles.rowField}
                options={{
                    clearable: false,
                    labelKey: 'abbreviation',
                    className: 'inline short',
                    placeholder: 'State*',
                    required: true
                }}
            />

            <TextInput source="zip" label="Zip Code"
                       validate={[regex(/^\d{5}$/, 'Must be a valid Zip Code')]}
                       style={{...viewStyles.rowField, ...styles.fieldSmall}}/>

            <TextInput source="country" label="Country" style={viewStyles.rowField}/>

            <TextInput source="telephone" label="Telephone"
                       validate={[regex(/^\d{10}$/, 'Must be 10 digits')]}
                       style={viewStyles.rowField}/>

            <SelectInput source="active" label="Loyalty Account is Active"
                         validate={[required]}
                         choices={[
                             {id: true, name: 'Yes'},
                             {id: false, name: 'No'}
                         ]}/>
            <span style={viewStyles.fieldDescription}>
                Select Yes or No.
                <TooltipIcon dataFor="ttActive"/>
                <ReactTooltip id="ttActive" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    If <em>Active</em>, this Loyalty Account may be credited during purchases on the POS System.
                    <br/>
                    If <em>Inactive</em>, this Loyalty Account may not be credited during purchases on the POS System.
                </ReactTooltip>
            </span>

            <LongTextInput source="notes" label="Account Notes"/>
            <span style={viewStyles.fieldDescription}>
                Enter any notes regarding this Account.
            </span>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <span style={viewStyles.fieldHeader}>
                Reward Points and Rank
                <TooltipIcon dataFor="ttReward"/>
                <ReactTooltip id="ttReward" type="light" effect="solid" multiline={true} border={true}
                              className="tooltip">
                    <strong>Reward Points</strong> and <strong>Rank Points</strong> are accumulated when a customer
                    applies a Loyalty Account during purchase. To adjust Points, enter a value to add or subtract,
                    not a new total.
                </ReactTooltip>
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <TextField source="rank.name" label="Current Rank" style={viewStyles.rowShowFieldWide}/>
            <TextField source="current_rank_points" label="Current Rank Points" style={viewStyles.rowShowFieldWide}/>
            <TextField source="current_reward_points" label="Current Reward Points"
                       style={viewStyles.rowShowFieldWide}/>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <LoyaltyAccountAccessFields formType="edit"/>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <span style={viewStyles.fieldHeader}>Account Locks</span>

            <SelectInput source="fraud_lock" label="Fraud Locked"
                         style={viewStyles.rowField}
                         choices={[
                             {id: true, name: 'True'},
                             {id: false, name: 'False'}
                         ]}/>
            <SelectInput source="temp_lock" label="Temp Locked"
                         style={viewStyles.rowField}
                         choices={[
                             {id: true, name: 'True'},
                             {id: false, name: 'False'}
                         ]}/>
            <SelectInput source="override_locks" label="Override Locks"
                         style={viewStyles.rowField}
                         choices={[
                             {id: true, name: 'True'},
                             {id: false, name: 'False'}
                         ]}/>

            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>

            <span style={viewStyles.fieldInstructions}>
                    After clicking <strong>Save</strong>, you will be redirected to the Loyalty Accounts Search form.
            </span>
            <hr style={{...viewStyles.break, ...viewStyles.paddingBottom}}/>
        </SimpleForm>
    </Edit>
);



// WEBPACK FOOTER //
// ./src/Fuse/Loyalty/account.js